import { ContextTracker, type Stack } from '@lezer/lr';

class LastAcceptedStatement {
  lastToken: string | null;
  pos: number | null;
  validExpression: boolean | undefined;

  constructor(
    lastToken: string | null,
    pos: number | null,
    validExpression?: boolean,
  ) {
    this.lastToken = lastToken;
    this.pos = pos;
    this.validExpression = validExpression;
  }
}

const trackTokens = [
  'expression',
  'comparison',
  'ComparisonOperator',
  'InclusionOperator',
  'SearchOperator',
  'Identifier',
  'KeyIdentifier',
  'JsonPathBase',
  'PositionOperator',
  'ClosingBracket',
  '","',
];

export const trackLastToken = new ContextTracker({
  start: new LastAcceptedStatement(null, null),
  reduce(context: LastAcceptedStatement, term: number, stack: Stack) {
    const termName = stack.parser.getName(term);

    if (trackTokens.includes(termName)) {
      return new LastAcceptedStatement(termName, stack.pos);
    }

    return context;
  },
  shift(context: LastAcceptedStatement, term: number, stack: Stack) {
    const termName = stack.parser.getName(term);

    // In case an Identifier is being shifted and we already encountered a comparison operator before,
    // we can assume that we have a valid expression even though it may not yet be reduced.
    // E.g.:
    // b =c"*"
    // When at the first doublequote, the previous expression is not yet reduced, but we still want to
    // accept free text for "*". For that to work in combination with trailing position operators, we
    // store the information that it may be a valid expression separately.
    if (
      context.lastToken === 'ComparisonOperator' &&
      termName === 'Identifier'
    ) {
      return new LastAcceptedStatement(context.lastToken, context.pos, true);
    }

    // Covering an edge-case where a string directly follows a string value.
    // The second string should be accepted as free text, but the first string is not yet reduced in this case.
    // E.g.:
    // a = "b""c"
    if (context.lastToken === 'ComparisonOperator' && termName === 'String') {
      return new LastAcceptedStatement(termName, stack.pos);
    }

    if (trackTokens.includes(termName)) {
      return new LastAcceptedStatement(termName, stack.pos);
    }

    return context;
  },
  strict: false,
});
